/**
 * Onboarding service layer.
 *
 * All Firestore reads/writes for the onboarding module funnel through here so
 * that components stay free of direct firebase/firestore calls (per Section
 * 14 of the Phase 1 build doc).
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from 'firebase/storage';
import { db, storage, auth } from '../../../firebase';

const FN_BASE = '/.netlify/functions';

/** Get a fresh ID token for the current user, or throw if unauthenticated. */
async function getIdToken() {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return await user.getIdToken();
}

/** Read a single onboarding template. */
export async function getTemplate(templateId) {
  const snap = await getDoc(doc(db, 'onboarding_templates', templateId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** Read a single policy document. */
export async function getPolicyDocument(policyId) {
  const snap = await getDoc(doc(db, 'policy_documents', policyId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Server-side lazy-seed / repair of a policy_document from canonical
 * content. Returns the fresh policy, or null if the id has no canonical
 * content and no Firestore doc.
 */
export async function ensurePolicyDocument(policyId) {
  const idToken = await getIdToken();
  const res = await fetch(`${FN_BASE}/ensure-policy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken, policyId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to ensure policy');
  return data.policy || null;
}

/** Read a single checklist item. */
export async function getChecklistItem(itemId) {
  const snap = await getDoc(doc(db, 'checklist_items', itemId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// where(employee_id ==) + orderBy(order) would require a Firestore composite
// index. We sort client-side instead so this works on a fresh project without
// deploying indexes.
function byOrderAsc(a, b) {
  return (a.order || 0) - (b.order || 0);
}

/** List all checklist items for an employee, ordered. */
export async function listChecklistItems(employeeId) {
  const q = query(
    collection(db, 'checklist_items'),
    where('employee_id', '==', employeeId)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort(byOrderAsc);
}

/** Subscribe to checklist items for an employee in real time. */
export function subscribeChecklistItems(employeeId, cb) {
  const q = query(
    collection(db, 'checklist_items'),
    where('employee_id', '==', employeeId)
  );
  return onSnapshot(
    q,
    (snap) => {
      cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort(byOrderAsc));
    },
    (err) => {
      console.error('subscribeChecklistItems failed:', err);
      cb([]);
    }
  );
}

/** Read onboarding progress for an employee. */
export async function getProgress(employeeId) {
  const snap = await getDoc(doc(db, 'onboarding_progress', employeeId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** Subscribe to onboarding progress. */
export function subscribeProgress(employeeId, cb) {
  return onSnapshot(doc(db, 'onboarding_progress', employeeId), (snap) => {
    cb(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
}

/** List employees currently in onboarding. */
export async function listOnboardingEmployees() {
  const q = query(collection(db, 'employees'), where('status', '==', 'onboarding'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Read a signature record. */
export async function getSignature(signatureId) {
  const snap = await getDoc(doc(db, 'signatures', signatureId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** Read an uploaded document record. */
export async function getUploadedDocument(documentId) {
  const snap = await getDoc(doc(db, 'uploaded_documents', documentId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Create a signature via the server-side createSignature function.
 * @returns {Promise<{ signatureId: string, snapshotUrl: string }>}
 */
export async function createSignature({ employeeId, policyDocumentId, typedName, checklistItemId }) {
  const idToken = await getIdToken();
  const res = await fetch(`${FN_BASE}/create-signature`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken, employeeId, policyDocumentId, typedName, checklistItemId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to create signature');
  return data;
}

/**
 * Provision a new onboarding workflow for an employee.
 */
export async function createOnboardingForEmployee({ employeeId, templateId }) {
  const idToken = await getIdToken();
  const res = await fetch(`${FN_BASE}/create-onboarding`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken, employeeId, templateId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to provision onboarding');
  return data;
}

/**
 * Resend the welcome email to an employee. Triggers a Firebase password-reset
 * email so the employee can set a new password and sign in. Use after a manager
 * tells you the employee misplaced their original credentials.
 */
export async function resendWelcomeEmail(employeeId) {
  const idToken = await getIdToken();
  const res = await fetch(`${FN_BASE}/resend-employee-welcome`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken, employeeId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to resend welcome email');
  return data;
}

/**
 * Re-send the "your onboarding checklist is ready" email using the
 * employee's existing checklist + template. Use when the employee says
 * they missed or deleted the first one.
 */
export async function resendChecklistEmail(employeeId) {
  const idToken = await getIdToken();
  const res = await fetch(`${FN_BASE}/resend-checklist-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken, employeeId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to resend checklist email');
  return data;
}

/**
 * Send the "first day welcome" email — what to wear, what to bring for
 * I-9. Typically triggered manually a day or two before the hire date.
 */
export async function sendFirstDayWelcome(employeeId) {
  const idToken = await getIdToken();
  const res = await fetch(`${FN_BASE}/send-first-day-welcome`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken, employeeId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to send first-day welcome');
  return data;
}

/**
 * Delete an employee's onboarding (checklist + progress). Pass
 * `deleteEmployee: true` to also remove the employees/{id} document.
 */
export async function deleteEmployeeOnboarding(employeeId, { deleteEmployee = false } = {}) {
  const idToken = await getIdToken();
  const res = await fetch(`${FN_BASE}/delete-employee-onboarding`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken, employeeId, deleteEmployee }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to delete onboarding');
  return data;
}

/** List active onboarding templates available to assign. */
export async function listActiveTemplates() {
  const q = query(collection(db, 'onboarding_templates'), where('active', '==', true));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** List active policy documents available to assign individually. */
export async function listActivePolicyDocuments() {
  const q = query(collection(db, 'policy_documents'), where('active', '==', true));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Assign one or more policies to an existing employee so they can sign them
 * on file. Creates checklist_items records and notifies the employee.
 */
export async function assignPoliciesToEmployee({ employeeId, policyIds }) {
  const idToken = await getIdToken();
  const res = await fetch(`${FN_BASE}/assign-policies`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken, employeeId, policyIds }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to assign policies');
  return data;
}

/** Trigger server-side progress recalculation. Idempotent. */
export async function recalculateProgress(employeeId) {
  const idToken = await getIdToken();
  const res = await fetch(`${FN_BASE}/recalculate-progress`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken, employeeId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to recalculate progress');
  return data;
}

/**
 * Mark a manager-driven checklist item as completed.
 */
export async function markManagerItemComplete({ itemId, completedBy }) {
  await updateDoc(doc(db, 'checklist_items', itemId), {
    status: 'completed',
    completed_at: serverTimestamp(),
    completed_by: completedBy || null,
  });
  // Pull the item to know which employee to recalc.
  const item = await getChecklistItem(itemId);
  if (item?.employee_id) await recalculateProgress(item.employee_id);
}

/**
 * Delete a checklist item (manager/owner only — enforced by firestore.rules).
 */
export async function deleteChecklistItem(itemId) {
  const item = await getChecklistItem(itemId);
  await deleteDoc(doc(db, 'checklist_items', itemId));
  if (item?.employee_id) await recalculateProgress(item.employee_id);
}

/**
 * Upload a document for a checklist item.
 *
 * @param {Object} args
 * @param {File} args.file
 * @param {string} args.employeeId
 * @param {string} args.checklistItemId
 * @param {string} args.documentType
 * @param {(progress: number) => void} [args.onProgress]
 * @returns {Promise<{ documentId: string, fileUrl: string }>}
 */
export async function uploadChecklistDocument({
  file, employeeId, checklistItemId, documentType, onProgress,
}) {
  const documentId = `${employeeId}_${checklistItemId}_${Date.now()}`;
  const ext = file.name.split('.').pop().toLowerCase();
  const path = `employees/${employeeId}/uploads/${documentId}.${ext}`;
  const ref = storageRef(storage, path);

  const fileUrl = await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(ref, file, { contentType: file.type });
    task.on(
      'state_changed',
      (snap) => {
        if (onProgress && snap.totalBytes > 0) {
          onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
        }
      },
      reject,
      async () => resolve(await getDownloadURL(task.snapshot.ref))
    );
  });

  // Write metadata via direct Firestore (rules permit self-employee writes).
  const { setDoc } = await import('firebase/firestore');
  await setDoc(doc(db, 'uploaded_documents', documentId), {
    employee_id: employeeId,
    document_type: documentType,
    file_url: fileUrl,
    file_name: file.name,
    file_size_bytes: file.size,
    uploaded_at: serverTimestamp(),
    reviewed_at: null,
    reviewed_by: null,
    checklist_item_id: checklistItemId,
  });

  // Mark checklist item completed.
  await updateDoc(doc(db, 'checklist_items', checklistItemId), {
    status: 'completed',
    completed_at: serverTimestamp(),
    completed_by: auth.currentUser?.uid || null,
    metadata: { upload_id: documentId, file_url: fileUrl },
  });

  // Recalculate progress.
  await recalculateProgress(employeeId);

  return { documentId, fileUrl };
}

// ---------------------------------------------------------------------------
// Admin: template & policy_document management
//
// Firestore rules restrict writes here to role=owner. Deletes are denied on
// both collections, so "remove" is implemented as soft-delete (active=false)
// for templates and policy_documents. Items inside a template are stored as
// an array on the template doc and can be hard-removed via updateDoc.
// ---------------------------------------------------------------------------

/** List every onboarding template (active and inactive). */
export async function listAllTemplates() {
  const snap = await getDocs(collection(db, 'onboarding_templates'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Create a new onboarding template.
 *
 * @param {Object} args
 * @param {string} args.id              Doc id, e.g. "commercial_cleaner_v1".
 * @param {string} args.name
 * @param {string} args.version
 * @param {boolean} [args.active]
 * @param {Array}  [args.items]
 */
export async function createTemplate({ id, name, version, active = true, items = [] }) {
  if (!id) throw new Error('Template id is required');
  if (!name) throw new Error('Template name is required');
  // Refuse to clobber an existing doc.
  const existing = await getDoc(doc(db, 'onboarding_templates', id));
  if (existing.exists()) throw new Error(`Template "${id}" already exists`);
  await setDoc(doc(db, 'onboarding_templates', id), {
    name,
    version: version || '1.0.0',
    active,
    items: Array.isArray(items) ? items : [],
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
}

/** Update the metadata (name, version, active flag) of a template. */
export async function updateTemplateMeta(templateId, { name, version, active }) {
  const patch = { updated_at: serverTimestamp() };
  if (name !== undefined) patch.name = name;
  if (version !== undefined) patch.version = version;
  if (active !== undefined) patch.active = active;
  await updateDoc(doc(db, 'onboarding_templates', templateId), patch);
}

/**
 * Replace the entire items array on a template. Caller is responsible for
 * preserving items it wants to keep — used by the editor when adding,
 * editing, reordering, or removing items.
 */
export async function setTemplateItems(templateId, items) {
  if (!Array.isArray(items)) throw new Error('items must be an array');
  await updateDoc(doc(db, 'onboarding_templates', templateId), {
    items,
    updated_at: serverTimestamp(),
  });
}

/** List every policy document (active and inactive). */
export async function listAllPolicyDocuments() {
  const snap = await getDocs(collection(db, 'policy_documents'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Create a new policy document. Initializes the versions[] history so the
 * append-only firestore.rules invariant holds on future edits.
 */
export async function createPolicyDocument({ id, title, body_markdown, pdf_url = '', effective_date, version = '1.0.0' }) {
  if (!id) throw new Error('Policy id is required');
  if (!title) throw new Error('Policy title is required');
  if (typeof body_markdown !== 'string' || !body_markdown.trim()) {
    throw new Error('Policy body is required');
  }
  const existing = await getDoc(doc(db, 'policy_documents', id));
  if (existing.exists()) throw new Error(`Policy "${id}" already exists`);
  const now = new Date().toISOString();
  await setDoc(doc(db, 'policy_documents', id), {
    id,
    title,
    current_version: version,
    effective_date: effective_date || now.slice(0, 10),
    body_markdown,
    pdf_url,
    versions: [
      { version, body_markdown, pdf_url, effective_date: effective_date || now.slice(0, 10), created_at: now },
    ],
    active: true,
  });
}

/**
 * Edit a policy document. If the body or pdf_url change, append a new entry
 * to versions[] so the append-only audit trail is preserved. Title and
 * active changes alone do not bump the version.
 *
 * @param {string} policyId
 * @param {Object} patch              { title?, body_markdown?, pdf_url?, active?, version?, effective_date? }
 */
export async function updatePolicyDocument(policyId, patch) {
  const snap = await getDoc(doc(db, 'policy_documents', policyId));
  if (!snap.exists()) throw new Error(`Policy "${policyId}" not found`);
  const current = snap.data();
  const versions = Array.isArray(current.versions) ? current.versions : [];

  const next = { ...current };
  if (patch.title !== undefined) next.title = patch.title;
  if (patch.active !== undefined) next.active = patch.active;

  const bodyChanged = patch.body_markdown !== undefined && patch.body_markdown !== current.body_markdown;
  const pdfChanged  = patch.pdf_url !== undefined && (patch.pdf_url || '') !== (current.pdf_url || '');

  if (bodyChanged || pdfChanged) {
    const newVersion = patch.version || bumpPatchVersion(current.current_version || '1.0.0');
    const effective = patch.effective_date || new Date().toISOString().slice(0, 10);
    next.current_version = newVersion;
    next.effective_date = effective;
    next.body_markdown = patch.body_markdown ?? current.body_markdown;
    next.pdf_url = patch.pdf_url ?? current.pdf_url ?? '';
    next.versions = [
      ...versions,
      {
        version: newVersion,
        body_markdown: next.body_markdown,
        pdf_url: next.pdf_url,
        effective_date: effective,
        created_at: new Date().toISOString(),
      },
    ];
  }

  await setDoc(doc(db, 'policy_documents', policyId), next);
}

function bumpPatchVersion(v) {
  const parts = String(v).split('.').map((n) => parseInt(n, 10));
  while (parts.length < 3) parts.push(0);
  parts[2] = (isFinite(parts[2]) ? parts[2] : 0) + 1;
  return parts.map((n) => (isFinite(n) ? n : 0)).join('.');
}
